import { motion } from "framer-motion";

/* Classic sedan silhouette — thin, editorial. */
const ClassicCar = ({ className = "" }) => (
  <svg
    viewBox="0 0 260 70"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    {/* body */}
    <path
      d="M8 52 L22 52 
         C24 44 32 40 42 40 
         L70 40 
         L92 22 
         C100 18 112 16 128 16 
         L162 16 
         C182 16 198 22 214 34 
         L236 40 
         C246 42 250 46 250 52 
         L244 52
         M40 52 L216 52"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    {/* window line */}
    <path
      d="M96 22 L118 40 M158 22 L138 40 M118 22 L138 22"
      fill="none"
      stroke="currentColor"
      strokeWidth="1"
      opacity="0.55"
    />
    {/* front wheel */}
    <circle cx="34" cy="53" r="8.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="34" cy="53" r="2.5" fill="currentColor" />
    {/* rear wheel */}
    <circle cx="222" cy="53" r="8.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="222" cy="53" r="2.5" fill="currentColor" />
    {/* headlamp */}
    <circle cx="248" cy="42" r="1.6" fill="currentColor" />
  </svg>
);

export default function CarBand({ variant = "dark" }) {
  const dark = variant === "dark";
  return (
    <section
      data-testid="car-band"
      aria-hidden="true"
      className={`relative overflow-hidden ${dark ? "bg-forest-950 border-y border-bone-100/10" : "bg-forest-800/40"}`}
    >
      {/* subtle grain */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.06]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 240 240' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />

      <div className="relative py-14 md:py-20">
        {/* label */}
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 mb-10 md:mb-14 flex items-center justify-between">
          <div
            className={`overline ${dark ? "text-terracotta-400" : "text-terracotta-600"}`}
          >
            On the road with you
          </div>
          <div
            className={`hidden md:block font-serif italic text-lg ${dark ? "text-bone-100/70" : "text-forest-900/70"}`}
          >
            Coverage that keeps pace.
          </div>
        </div>

        {/* road */}
        <div className="relative h-24">
          {/* horizon hairline */}
          <div
            className={`absolute left-0 right-0 bottom-6 h-px ${dark ? "bg-bone-100/25" : "bg-forest-900/20"}`}
          />
          {/* dashed road markings — animated */}
          <div
            className="absolute left-0 right-0 bottom-[22px] h-[2px] road-dashes"
            style={{
              backgroundImage: `repeating-linear-gradient(to right, ${
                dark ? "rgba(205, 170, 110, 0.55)" : "rgba(176, 138, 74, 0.55)"
              } 0 22px, transparent 22px 60px)`,
            }}
          />

          {/* Car A — drifts right→left slowly */}
          <motion.div
            initial={{ x: "110vw" }}
            animate={{ x: "-30vw" }}
            transition={{ duration: 32, ease: "linear", repeat: Infinity }}
            className="absolute bottom-0 will-change-transform"
          >
            <ClassicCar
              className={`w-[210px] md:w-[280px] ${dark ? "text-bone-100" : "text-forest-900"}`}
            />
          </motion.div>

          {/* Car B — smaller, opposite depth, slower */}
          <motion.div
            initial={{ x: "-30vw" }}
            animate={{ x: "115vw" }}
            transition={{ duration: 46, ease: "linear", repeat: Infinity, delay: 4 }}
            className="absolute bottom-1 will-change-transform"
          >
            <ClassicCar
              className={`w-[150px] md:w-[190px] opacity-40 ${
                dark ? "text-bone-100" : "text-forest-900"
              }`}
            />
          </motion.div>
        </div>
      </div>

      <style>{`
        @keyframes road-scroll {
          from { background-position-x: 0; }
          to { background-position-x: -60px; }
        }
        .road-dashes {
          animation: road-scroll 1.4s linear infinite;
        }
      `}</style>
    </section>
  );
}
